import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import { AlertCircle } from "lucide-react";
import GoogleLoginButton from "@/components/GoogleLoginButton";

export default function Login() {
  const { login } = useAuth();
  const { t } = useLang();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      await login(email, password);
      nav("/");
    } catch (err) {
      setError(err.response?.data?.detail || "Login failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-4 py-12" data-testid="login-page">
      <div className="w-full max-w-md border border-zinc-800 bg-[var(--wb-surface)] p-8">
        <div className="flex items-center gap-2 mb-8">
          <img src="/tigik-logo.png" alt="Tigik" className="w-10 h-10 object-cover border border-gold-600/40" />
          <span className="font-display font-black text-xl">TIGIK</span>
        </div>
        <h1 className="font-display font-bold text-3xl mb-1">{t.auth.loginTitle}</h1>
        <p className="text-sm text-zinc-500 mb-6 font-mono uppercase tracking-wider">→ access tigik</p>

        <GoogleLoginButton next="/" />
        <div className="flex items-center gap-3 my-5">
          <div className="h-px bg-zinc-800 flex-1" />
          <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-600">alebo</span>
          <div className="h-px bg-zinc-800 flex-1" />
        </div>

        <form onSubmit={submit} className="space-y-4" data-testid="login-form">
          <div>
            <label className="block text-xs font-mono uppercase tracking-wider text-zinc-500 mb-1">{t.auth.email}</label>
            <input
              data-testid="login-email"
              type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
              className="wb-input w-full px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-mono uppercase tracking-wider text-zinc-500 mb-1">{t.auth.password}</label>
            <input
              data-testid="login-password"
              type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
              className="wb-input w-full px-3 py-2 text-sm"
            />
          </div>
          {error && (
            <div data-testid="login-error" className="flex items-center gap-2 p-2 border border-red-900 bg-red-950/30 text-red-400 text-sm">
              <AlertCircle className="w-4 h-4" /> {error}
            </div>
          )}
          <button
            data-testid="login-submit"
            type="submit" disabled={loading}
            className="wb-btn-primary w-full py-3 text-sm disabled:opacity-50"
          >
            {loading ? t.common.loading : t.auth.submitLogin}
          </button>
        </form>
        <div className="mt-4 text-center">
          <Link to="/forgot-password" data-testid="forgot-password-link" className="text-xs font-mono uppercase tracking-wider text-zinc-500 hover:text-gold-400">
            Zabudnuté heslo?
          </Link>
        </div>
        <div className="mt-6 text-sm text-zinc-500 text-center">
          {t.auth.noAccount} <Link to="/register" className="text-gold-500 hover:text-gold-400" data-testid="goto-register">{t.auth.submitRegister}</Link>
        </div>
      </div>
    </div>
  );
}
